// Configuration des cibles et seuils pour tous les ratios
// Ces valeurs sont modifiables par l'utilisateur

export const TARGETS_CONFIG = {
  // ============== BLOC RENTABILITÉ - GLOBAL ==============
  rentabilite: {
    marge_brute: {
      label: "Marge brute (entreprise globale)",
      unit: "%",
      description: `La marge brute GLOBALE mesure la rentabilité de tes opérations terrain pour l'ensemble de l'entreprise sur une période donnée (mois, année).

**Formule** : (CA - Coûts directs) / CA × 100

**Ce qu'elle inclut comme coûts** : Salaires terrain, matériaux, peinture, déplacements chantier, sous-traitants.

**Ce qu'elle exclut** : Loyer, assurances, salaires admin, intérêts, impôts, dividendes, investissements.

**Pourquoi c'est important** : Si ta marge brute est faible, peu importe ton volume de ventes, tu ne seras jamais rentable. C'est la fondation de ta rentabilité.

**Question clé** : « Est-ce que mes chantiers sont rentables avant de payer le bureau ? »`,
      thresholds: { green: 45, orange: 38, red: 0 },
      direction: "higher_is_better"
    },
    marge_brute_projet: {
      label: "Marge brute par projet (dossier individuel)",
      unit: "%",
      description: `La marge brute PAR PROJET mesure la rentabilité d'un dossier client individuel — ce qui sert à classer les projets vert/orange/rouge dans la page "Marges par projet".

**Formule** : (Montant contrat - Coûts directs du chantier) / Montant contrat × 100

**Ce qu'elle inclut comme coûts** : Heures punchées × coût employeur, matériaux entrepôt, produits soumission.

**Pourquoi c'est important** : Permet d'identifier les types de chantiers les plus profitables (intérieur vs extérieur, patio vs sous-sol, etc.) et d'ajuster ta tarification.

**Question clé** : « Est-ce que CE projet spécifique est rentable ? »

**Distinction** : Ce seuil s'applique aux dossiers individuels. La "Marge brute globale" ci-dessus s'applique aux KPIs mensuels/annuels de l'entreprise.`,
      thresholds: { green: 40, orange: 30, red: 0 },
      direction: "higher_is_better"
    },
    frais_fixes_pct: {
      label: "Frais fixes % du CA",
      unit: "%",
      description: `Ce ratio mesure le poids de ta structure administrative par rapport à ton volume d'affaires.

**Formule** : Frais fixes / CA × 100

**Ce qu'on considère comme frais fixes** : Loyer, assurances, salaires admin/comptabilité, télécom, abonnements SaaS, intérêts sur dettes, dépréciation.

**Pourquoi c'est important** : Une structure trop lourde mange tes profits même si tes chantiers sont rentables. Si tu grandis, tes frais fixes ne devraient pas grandir au même rythme.

**Question clé** : « Est-ce que ma structure administrative est adaptée à mon volume d'affaires ? »

**Exemple** : À 1 350 000$ de CA, 15% = 202 500$ de frais fixes max par année.`,
      thresholds: { green: 15, orange: 20, red: 100 },
      direction: "lower_is_better"
    },
    marge_nette_avant_impots: {
      label: "Marge nette avant impôts",
      unit: "%",
      description: `La marge nette avant impôts montre ce qui reste VRAIMENT après avoir payé TOUTES les dépenses de l'entreprise, mais avant de payer le gouvernement.

**Formule** : (CA - Coûts directs - Frais fixes) / CA × 100

**Pourquoi c'est important** : C'est le vrai indicateur de performance de ton entreprise. Une marge brute élevée avec des frais fixes excessifs = mauvaise marge nette.

**Question clé** : « Combien il me reste sur chaque dollar de vente avant les impôts ? »

**Ton objectif** : 18% minimum pour pouvoir payer dividendes + garder 10% non distribué.`,
      thresholds: { green: 18, orange: 12, red: 0 },
      direction: "higher_is_better"
    },
    marge_nette_apres_impots: {
      label: "Marge nette après impôts",
      unit: "%",
      description: `C'est le profit final, ce qui reste après avoir payé TOUT le monde, incluant le gouvernement.

**Formule** : Résultat après impôts / CA × 100

**Ce qui se passe avec ce montant** : Il sert à payer les dividendes aux actionnaires ET à réinvestir dans l'entreprise.

**Ton cas spécifique** : Avec 72 000$/an de dividendes + 10% de résultat non distribué, tu as besoin d'au moins 16% de marge nette après impôts sur 1.35M$ de CA.

**Question clé** : « Ai-je assez de profit pour me payer ET faire croître l'entreprise ? »`,
      thresholds: { green: 16, orange: 12, red: 0 },
      direction: "higher_is_better"
    },
    resultat_non_distribue: {
      label: "Résultat non distribué (YTD)",
      unit: "%",
      description: `Le résultat non distribué mesure la capacité de l'entreprise à créer de la richesse et la conserver pour croître.

**Formule** : (Profit après impôts - Dividendes versés) / CA cumulatif × 100

**Pourquoi c'est NON NÉGOCIABLE à 10%** : 
- Constitue un fonds de roulement solide
- Permet d'investir sans s'endetter
- Protège contre les imprévus (ex: mois creux)
- Augmente la valeur de l'entreprise

**Question clé** : « Est-ce que l'entreprise s'enrichit ou est-ce qu'on sort tout l'argent ? »`,
      thresholds: { green: 10, orange: 7, red: 0 },
      direction: "higher_is_better",
      non_negotiable: true
    },
    roa_mensuel: {
      label: "ROA (mensuel)",
      unit: "%",
      description: `Le ROA mesure l'efficacité avec laquelle tu utilises tes actifs (équipements, véhicules, cash) pour générer du profit.

**Formule** : Résultat net après impôts / Actifs totaux × 100

**Pourquoi ça varie selon la période** :
- Mensuel : On divise par 1 mois → cible 0.8%
- Trimestriel : On cumule 3 mois → cible 2.5%
- Annuel : On cumule 12 mois → cible 10%

**Exemple concret** : Si tu as 500 000$ d'actifs et que tu génères 50 000$ de profit/an, ton ROA = 10%. Ça veut dire que chaque dollar investi dans l'entreprise génère 10 cents de profit.

**Question clé** : « Est-ce que mes équipements, mes véhicules et mon cash travaillent efficacement pour moi ? »`,
      thresholds: { green: 0.8, orange: 0.4, red: 0 },
      direction: "higher_is_better"
    },
    roa_trimestriel: {
      label: "ROA (trimestriel)",
      unit: "%",
      thresholds: { green: 2.5, orange: 1.2, red: 0 },
      direction: "higher_is_better"
    },
    roa_annuel: {
      label: "ROA (annuel)",
      unit: "%",
      thresholds: { green: 10, orange: 5, red: 0 },
      direction: "higher_is_better"
    }
  },

  // ============== BLOC RENTABILITÉ - PAR DÉPARTEMENT ==============
  rentabilite_departement: {
    PRQC: {
      marge_brute: { green: 45, orange: 38, red: 0 },
      revenu_par_employe: { green: 8500, orange: 6500, red: 0 }
    },
    LMDS: {
      marge_brute: { green: 25, orange: 18, red: 0 },
      revenu_par_employe: { green: 11500, orange: 9000, red: 0 }
    },
    LOCATION: {
      marge_brute: { green: 70, orange: 55, red: 0 },
      revenu_par_employe: null // N/A
    }
  },

  // ============== BLOC ANALYSE DES REVENUS ==============
  revenus: {
    pct_objectif_ca: {
      label: "% Objectif CA atteint",
      unit: "%",
      description: `Compare tes revenus réels à l'objectif que tu t'es fixé pour la période.

**Formule** : CA réel / Objectif CA × 100

**Pourquoi c'est important** : Permet de savoir si tu es sur la bonne voie pour atteindre tes objectifs annuels. Un retard en début d'année peut souvent être rattrapé en haute saison, mais pas l'inverse.

**Question clé** : « Est-ce que je suis en avance ou en retard sur mes objectifs de vente ? »

**Exemple** : Objectif janvier = 80 000$, CA réel = 72 000$ → 90% atteint (🟠 Orange)`,
      thresholds: { green: 95, orange: 80, red: 0 },
      direction: "higher_is_better"
    },
    pct_ca_cash: {
      label: "% CA en argent cash",
      unit: "%",
      description: `Mesure la proportion de tes revenus reçus en argent comptant plutôt que par virement ou chèque.

**Formule** : Revenus cash / CA total × 100

**Pourquoi surveiller ce ratio** :
- Le cash n'a pas de trace bancaire directe
- Un % trop élevé peut attirer l'attention du fisc
- Risque de perte/vol
- Plus difficile à comptabiliser

**Question clé** : « Est-ce que ma proportion de cash reste dans une zone raisonnable ? »

**Note importante** : Les revenus cash sont quand même inclus dans TOUS tes calculs de ratios, ils ne sont juste pas taxables de la même façon.`,
      thresholds: { green: 15, orange: 25, red: 100 },
      direction: "lower_is_better"
    },
    croissance_ca_yoy: {
      label: "Croissance CA vs année précédente",
      unit: "%",
      thresholds: { green: 10, orange: 0, red: -100 },
      direction: "higher_is_better"
    }
  },

  // ============== BLOC PRODUCTION / EFFICACITÉ ==============
  production: {
    pct_heures_terrain: {
      label: "% Heures terrain",
      unit: "%",
      description: `Mesure la proportion du temps total de l'entreprise passé sur des activités facturables (chantiers) vs administratives (bureau).

**Formule** : Heures terrain / (Heures terrain + Heures admin) × 100

**Pourquoi 80% est la cible** : 
- Les heures terrain génèrent des revenus
- Les heures admin sont nécessaires mais ne facturent pas
- Trop d'admin = structure inefficace
- Pas assez d'admin = chaos organisationnel

**Question clé** : « Est-ce qu'on passe assez de temps sur le terrain à faire rentrer de l'argent ? »

**Exemple** : 400 heures terrain + 100 heures admin = 80% terrain ✅`,
      thresholds: { green: 80, orange: 70, red: 0 },
      direction: "higher_is_better"
    },
    revenu_par_heure: {
      label: "Revenu / heure terrain",
      unit: "$",
      description: `Indique combien chaque heure de travail sur un chantier génère en revenus. C'est un indicateur direct de ta productivité et de ton positionnement de prix.

**Formule** : CA total / Heures terrain totales

**Ce qui influence ce ratio** :
- Tes prix (taux horaire ou prix forfaitaire)
- La vitesse d'exécution des équipes
- La complexité des projets
- Le gaspillage de temps sur chantier

**Pour peinture résidentielle au Québec** : 90$/heure est un bon objectif pour un travail de qualité. En dessous de 75$, tu es probablement sous-facturé ou inefficace.

**Question clé** : « Est-ce qu'on facture assez cher ou est-ce qu'on travaille assez vite ? »`,
      thresholds: { green: 90, orange: 75, red: 0 },
      direction: "higher_is_better"
    },
    efficacite_mo: {
      label: "Efficacité MO ($/heure)",
      unit: "$",
      description: `Mesure combien de PROFIT BRUT chaque heure de travail terrain génère. C'est le revenu/heure multiplié par la marge brute.

**Formule** : Profit brut / Heures terrain

**Relation avec le revenu/heure** : 
- Si revenu/heure = 90$ et marge brute = 45%
- Alors efficacité MO = 90$ × 45% = 40.50$

**Pourquoi c'est différent du revenu/heure** : Tu peux avoir un bon revenu/heure mais des coûts trop élevés. L'efficacité MO montre le vrai profit par heure travaillée.

**Question clé** : « Combien de profit brut chaque heure sur le terrain me rapporte-t-elle vraiment ? »`,
      thresholds: { green: 40, orange: 30, red: 0 },
      direction: "higher_is_better"
    },
    cout_mo_revenus: {
      label: "Coût MO / Revenus % (PRQC)",
      unit: "%",
      description: `Mesure quelle proportion de tes revenus part en salaires pour les employés terrain. C'est ton plus gros poste de dépenses variables.

**Formule** : Salaires terrain / CA × 100

**Pourquoi 40% et moins est vert** : Dans ton modèle avec des employés, la main-d'œuvre coûte cher. À 40%, il te reste 60% pour les matériaux (~12%), les frais fixes (~15%) et le profit (~33% avant ventilation).

**Ce qui fait monter ce ratio** :
- Salaires trop élevés
- Temps mort payé
- Mauvaise estimation des heures
- Employés qui travaillent lentement

**Question clé** : « Est-ce que ma masse salariale terrain est proportionnelle à mes revenus ? »`,
      thresholds: { green: 40, orange: 48, red: 100 },
      direction: "lower_is_better"
    },
    materiaux_revenus: {
      label: "Matériaux+Peinture / Revenus %",
      unit: "%",
      description: `Mesure le coût des matériaux consommés par rapport aux revenus générés. Indique si tu gères bien tes achats et ton gaspillage.

**Formule** : (Coût matériaux + Coût peinture) / CA × 100

**Ce qui fait monter ce ratio** :
- Gaspillage de peinture
- Vol ou perte de matériaux
- Mauvaise estimation des quantités
- Achats non négociés
- Retouches et reprises

**Question clé** : « Est-ce qu'on gaspille du matériel ou est-ce qu'on achète trop cher ? »

**Exemple** : Sur un contrat de 10 000$, tu devrais utiliser max 1 200$ de matériaux (12%).`,
      thresholds: { green: 12, orange: 18, red: 100 },
      direction: "lower_is_better"
    }
  },

  // ============== BLOC STRUCTURE & STABILITÉ ==============
  structure: {
    ratio_liquidite: {
      label: "Ratio de liquidité courante",
      unit: "x",
      description: `Mesure ta capacité à payer tes dettes à court terme (moins d'un an) avec tes actifs disponibles rapidement.

**Formule** : Actifs courants / Passifs courants

**Ce qu'on considère comme actifs courants** : Cash en banque, comptes clients à recevoir, inventaire.

**Ce qu'on considère comme passifs courants** : Comptes fournisseurs, cartes de crédit, portion court terme des prêts, taxes à payer.

**Interprétation** :
- = 1.0 : Tu as juste assez pour payer tes dettes → risqué
- = 1.5 : Tu as 1.50$ pour chaque 1$ de dette → confortable
- = 2.0+ : Tu as beaucoup de marge → très sécuritaire
- < 1.0 : Tu ne peux pas payer tes dettes court terme → problème urgent

**Question clé** : « Si tous mes créanciers me demandaient de payer demain, est-ce que je pourrais ? »`,
      thresholds: { green: 1.5, orange: 1.0, red: 0 },
      direction: "higher_is_better"
    },
    ratio_endettement_mensuel: {
      label: "Ratio d'endettement (mensuel)",
      unit: "%",
      description: `Mesure quelle proportion de tes actifs est financée par de la dette (vs tes propres fonds).

**Formule** : Dettes totales / Actifs totaux × 100

**Pourquoi ça varie selon la période** :
- En cours de mois, les dettes fluctuent (cartes de crédit, etc.)
- En fin d'année, tu as une image plus stable
- On tolère donc un peu plus en annuel qu'en mensuel

**Interprétation** :
- 35% : Pour chaque 100$ d'actifs, 35$ vient de la dette → sain
- 60% : Plus de la moitié de tes actifs appartient aux créanciers → attention
- 80%+ : Tu es très endetté → risqué

**Question clé** : « Quelle part de mon entreprise appartient réellement à moi vs à mes créanciers ? »`,
      thresholds: { green: 35, orange: 50, red: 100 },
      direction: "lower_is_better"
    },
    ratio_endettement_trimestriel: {
      label: "Ratio d'endettement (trimestriel)",
      unit: "%",
      thresholds: { green: 38, orange: 52, red: 100 },
      direction: "lower_is_better"
    },
    ratio_endettement_annuel: {
      label: "Ratio d'endettement (annuel)",
      unit: "%",
      thresholds: { green: 40, orange: 55, red: 100 },
      direction: "lower_is_better"
    },
    ratio_dettes_capitaux: {
      label: "Ratio dettes / capitaux propres",
      unit: "x",
      description: `Compare directement ce que tu dois vs ce que tu possèdes réellement. C'est le levier financier de ton entreprise.

**Formule** : Dettes totales / Capitaux propres

**Capitaux propres** = Actifs totaux - Dettes totales (ce qui t'appartient vraiment)

**Interprétation** :
- = 0.5 : Tu dois 50 cents pour chaque 1$ que tu possèdes → conservateur
- = 1.0 : Tu dois autant que tu possèdes → équilibré
- = 2.0 : Tu dois le double de ce que tu possèdes → agressif/risqué

**Question clé** : « Pour chaque dollar que je possède, combien est-ce que je dois ? »`,
      thresholds: { green: 0.8, orange: 1.5, red: 100 },
      direction: "lower_is_better"
    },
    taux_reinvestissement: {
      label: "Taux réinvestissement",
      unit: "%",
      description: `Mesure quelle proportion du profit tu gardes dans l'entreprise vs ce que tu sors en dividendes.

**Formule** : (Profit après impôts - Dividendes) / Profit après impôts × 100

**Ce ratio dit** : Sur le profit disponible, combien reste dans l'entreprise.

**Question clé** : « Est-ce que je construis l'entreprise ou est-ce que je sors tout l'argent ? »`,
      thresholds: { green: 40, orange: 55, red: 100 },
      direction: "lower_is_better"
    },
    jours_tresorerie: {
      label: "Jours de trésorerie",
      unit: " jours",
      description: `Combien de jours l'entreprise peut fonctionner avec le cash disponible.

**Formule** : Cash disponible / (Dépenses mensuelles / 30)

**Interprétation** :
- 45+ jours : Coussin confortable
- 25-44 jours : Acceptable mais surveillé
- < 25 jours : Risque de manquer de liquidités`,
      thresholds: { green: 45, orange: 25, red: 0 },
      direction: "higher_is_better"
    }
  },

  // ============== BLOC MARKETING / COMMERCIAL ==============
  marketing: {
    pct_objectif_soumissions: {
      label: "% Objectif soumissions",
      unit: "%",
      thresholds: { green: 95, orange: 80, red: 0 },
      direction: "higher_is_better"
    },
    pct_objectif_contrats: {
      label: "% Objectif contrats signés",
      unit: "%",
      thresholds: { green: 95, orange: 80, red: 0 },
      direction: "higher_is_better"
    },
    taux_signature: {
      label: "Taux de signature",
      unit: "%",
      description: `Mesure ton efficacité à convertir une soumission en contrat signé. C'est l'indicateur clé de ta force de vente.

**Formule** : Contrats signés / Soumissions envoyées × 100

**Ce qui influence ce ratio** :
- La qualité de tes leads (bien ciblés ou non)
- Tes prix vs la compétition
- Ta réputation / avis Google
- La qualité de ta présentation et suivi
- Le timing de ta réponse

**Moyenne du secteur réno résidentielle** : 25-35%

**Question clé** : « Sur 10 soumissions envoyées, combien deviennent des contrats ? »

**Exemple** : 30 soumissions, 12 contrats signés = 40% de taux de signature ✅`,
      thresholds: { green: 35, orange: 25, red: 0 },
      direction: "higher_is_better"
    },
    cac_google: {
      label: "CAC Google",
      unit: "$",
      description: `Le CAC mesure combien tu dépenses en publicité pour acquérir UN nouveau client payant.

**Formule** : Dépenses publicitaires Google / Nombre de nouveaux clients acquis via Google

**Ce qui fait monter ton CAC** :
- Mauvais ciblage publicitaire
- Annonces peu attrayantes
- Page d'atterrissage inefficace
- Concurrence élevée
- Saison morte

**Question clé** : « Combien ça me coûte pour obtenir un nouveau client via Google ? »`,
      thresholds: { green: 150, orange: 250, red: 10000 },
      direction: "lower_is_better"
    },
    cac_meta: {
      label: "CAC Meta",
      unit: "$",
      description: `Coût d'acquisition d'un client via Facebook/Instagram.

**Formule** : Dépenses publicitaires Meta / Nombre de nouveaux clients acquis via Meta`,
      thresholds: { green: 175, orange: 300, red: 10000 },
      direction: "lower_is_better"
    },
    ratio_ltv_cac: {
      label: "Ratio LTV:CAC",
      unit: ":1",
      description: `Compare la valeur totale qu'un client génère sur sa "vie" avec toi vs le coût pour l'acquérir. C'est LE ratio qui dit si ton marketing est rentable.

**Formule** : Valeur vie client (LTV) / Coût d'acquisition (CAC)

**LTV (Lifetime Value)** : Revenu total moyen qu'un client génère sur toute sa relation avec toi (incluant les retours et références).

**Interprétation** :
- < 1:1 : Tu perds de l'argent sur chaque client acquis
- 2:1 : Rentable mais serré
- 3:1 : Bon équilibre
- 5:1+ : Excellent, tu pourrais investir plus en marketing

**Question clé** : « Est-ce que chaque dollar investi en pub me rapporte assez sur le long terme ? »`,
      thresholds: { green: 3.5, orange: 2.0, red: 0 },
      direction: "higher_is_better"
    },
    romi: {
      label: "ROMI",
      unit: "%",
      description: `Le ROMI mesure la rentabilité réelle de ton marketing. Il répond à la question : « Est-ce que l'argent investi en marketing génère réellement du profit brut ? »

**Formule** : (Profit brut généré par le marketing - Coût marketing) / Coût marketing × 100

**Indique combien de dollars de profit brut sont générés pour chaque dollar investi en marketing.**

**Différence avec le LTV:CAC** :
- LTV:CAC parle de revenus
- ROMI parle de PROFIT BRUT (après avoir enlevé les coûts directs)

**Interprétation** :
- ROMI 100% : Tu doubles ta mise (1$ investi = 1$ de profit)
- ROMI 300% : Tu quadruples ta mise (1$ investi = 3$ de profit)
- ROMI 500%+ : Ton marketing est très rentable

**Question clé** : « Pour chaque dollar investi en publicité, combien de dollars de profit brut est-ce que je génère ? »

**Exemple** : 2 000$ en pub, génère 25 000$ de CA avec 45% de marge brute = 11 250$ de profit brut. ROMI = (11 250 - 2 000) / 2 000 × 100 = 462%`,
      thresholds: { green: 350, orange: 200, red: 0 },
      direction: "higher_is_better"
    },
    valeur_moyenne_contrat: {
      label: "Valeur moyenne contrat",
      unit: "$",
      description: `Le montant moyen de chaque contrat signé. Indique le type de projets que tu fais.

**Formule** : CA total / Nombre de contrats

**Pour peinture résidentielle** :
- < 2 500$ : Petits projets (1-2 pièces), volume élevé requis
- 4 500$+ : Projets moyens-gros (maison complète), meilleure rentabilité
- 10 000$+ : Gros projets (luxe, neuf), moins de volume mais très rentable

**Question clé** : « Est-ce que je fais des projets assez gros pour être efficace ? »`,
      thresholds: { green: 4500, orange: 2500, red: 0 },
      direction: "higher_is_better"
    }
  }
};

// Fonction pour obtenir la couleur selon la valeur et les seuils
export const getStatusColor = (value, config) => {
  if (value === null || value === undefined) return "gray";
  
  const { thresholds, direction } = config;
  
  if (direction === "higher_is_better") {
    if (value >= thresholds.green) return "green";
    if (value >= thresholds.orange) return "orange";
    return "red";
  } else {
    if (value <= thresholds.green) return "green";
    if (value <= thresholds.orange) return "orange";
    return "red";
  }
};

// Fonction pour obtenir la flèche de tendance
export const getTrendArrow = (current, previous) => {
  if (previous === null || previous === undefined || previous === 0) return null;
  
  const change = ((current - previous) / Math.abs(previous)) * 100;
  
  if (change > 2) return { direction: "up", value: change };
  if (change < -2) return { direction: "down", value: change };
  return { direction: "stable", value: change };
};

// Configuration fiscale
export const FISCAL_CONFIG = {
  tps_rate: 0.05,
  tvq_rate: 0.09975,
  combined_tax_rate: 0.14975,
  tax_rate_pme: 0.122,
  pme_threshold: 500000
};

// Départements
export const DEPARTMENTS = {
  PRQC: { name: "Peinture Résidentielle", color: "#064E3B" },
  LMDS: { name: "Le Marché du Store", color: "#0EA5E9" },
  LOCATION: { name: "Location Équipements", color: "#8B5CF6" },
  GLOBAL: { name: "Global", color: "#374151" }
};

// Catégories de dépenses
export const EXPENSE_CATEGORIES = {
  salaires_terrain: { label: "Salaires terrain", type: "variable", profit_level: "brut" },
  salaires_admin: { label: "Salaires admin", type: "fixe", profit_level: "marge" },
  materiaux: { label: "Matériaux", type: "variable", profit_level: "brut" },
  peinture: { label: "Peinture", type: "variable", profit_level: "brut" },
  essence: { label: "Essence", type: "variable", profit_level: "brut" },
  sous_traitants: { label: "Sous-traitants", type: "variable", profit_level: "brut" },
  loyer: { label: "Loyer", type: "fixe", profit_level: "marge" },
  assurances: { label: "Assurances", type: "fixe", profit_level: "marge" },
  telecom: { label: "Télécom", type: "fixe", profit_level: "marge" },
  saas: { label: "Abonnements SaaS", type: "fixe", profit_level: "marge" },
  publicite_google: { label: "Publicité Google", type: "variable", profit_level: "marge" },
  publicite_meta: { label: "Publicité Meta", type: "variable", profit_level: "marge" },
  interets: { label: "Intérêts", type: "fixe", profit_level: "marge" },
  depreciation: { label: "Dépréciation", type: "fixe", profit_level: "marge" },
  dividendes: { label: "Dividendes", type: "distribution", profit_level: "net" },
  autres: { label: "Autres", type: "variable", profit_level: "marge" }
};

// Types de cash entry
export const CASH_ENTRY_TYPES = {
  depot_revenu: { label: "Dépôt - Revenu client", category: "revenu" },
  retrait_salaire_terrain: { label: "Retrait - Salaire terrain", category: "depense", expense_type: "salaires_terrain" },
  retrait_salaire_admin: { label: "Retrait - Salaire admin", category: "depense", expense_type: "salaires_admin" },
  retrait_dividende: { label: "Retrait - Dividende", category: "distribution", expense_type: "dividendes" },
  retrait_materiel: { label: "Retrait - Matériel/Fournitures", category: "depense", expense_type: "materiaux" },
  retrait_autre: { label: "Retrait - Autre dépense", category: "depense", expense_type: "autres" }
};

export default TARGETS_CONFIG;
